import { isEmpty } from 'radash';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { GridRef } from '@/components/core/Grid/Grid';
import { Loader } from '@/components/core/Loader';
import { useFormReset } from '@/dialogs/item-editor/Context/FormResetContext';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { logger } from '@/services/logger';

import { useVisibleMemberAttributes } from '../../Collection/useMemberAttributes';
import useFocusOnAttribute from '../../hooks/useFocusOnAttribute';
import { useFocusOnNewRow } from '../../hooks/useFocusOnNewRow';
import { useIsAttributeReadonly } from '../../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../../hooks/usePlaceholder';
import { Layout } from '../../Layout/Layout';
import { EditorComponent } from '../../types';
import {
    useMultiReferenceColumns,
    useMultiReferenceData,
    useMultiReferenceForm,
    useMultiReferenceValidationUpdater} from '../hooks';
import { ReferenceItemOption } from '../types';
import {
    MultiReferenceDialogs,
    MultiReferenceGrid,
    MultiReferenceToolbar} from '.';

interface MultiReferenceComponentProps extends EditorComponent {

}

const MultiReferenceComponent = ({
    attribute,
    direction = 'vertical',
    fieldPath = attribute.name,
    hideLabel,
    hidden = false,
    readonly = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    labelClassName,
}: MultiReferenceComponentProps) => {

    const { dataModel } = useDataModel();
    const currentValue = useWatch({
        name: fieldPath
    });
    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const placeholder = usePlaceholder(attribute, readonly);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCreateNewItemDialogOpen, setIsCreateNewItemDialogOpen] = useState(false);
    const [newlyCreatedItem, setNewlyCreatedItem] = useState<ReferenceItemOption | null>(null);
    const newlyCreatedItemRef = useRef<ReferenceItemOption | null>(null);

    useEffect(() => {
        newlyCreatedItemRef.current = newlyCreatedItem;
    }, [newlyCreatedItem]);
    const { user } = useUser();
    const { organization } = useOrganization();

    const [enableSorting, setEnableSorting] = useState(true);
    const gridRef = useRef<GridRef>(null);

    const isReverseMultiReferenceAttribute = attribute.params?.reverseHierarchy || false;

    const referenceItemCategories = attribute.params?.references || attribute.params?.referenceCategories;

    const memberAttributes = useVisibleMemberAttributes(attribute, dataModel);

    // Use custom hooks - we need to call useMultiReferenceData first to get loadingInitialItems
    // but it depends on setValueWithDirtyState from useMultiReferenceForm
    // So we'll use a temporary loading state and update it
    const [tempLoadingState, setTempLoadingState] = useState(true);

    const {
        fields,
        remove,
        appendItem,
        setValueWithDirtyState,
        setValue,
        resetInitialLoad
    } = useMultiReferenceForm(fieldPath, isReverseMultiReferenceAttribute, tempLoadingState);

    const {
        loadingInitialItems,
        hierarchyLoading,
        hierarchyError,
        hierarchyData,
        currentItemPrimaryKey,
        extendReferencedItems,
        handleDataRestoration,
        cacheNewlyAddedItem,
        extendedDataCache
    } = useMultiReferenceData(
        attribute,
        fieldPath,
        // memberAttributes,
        setValueWithDirtyState,
        isReverseMultiReferenceAttribute
    );

    const { updateReferencedItemIds } = useMultiReferenceValidationUpdater({
        fieldPath,
        setValueWithDirtyState,
        extendedDataCache,
        newlyCreatedItemRef,
        setNewlyCreatedItem,
        hasNewlyCreatedItem: !!newlyCreatedItem
    });

    // Register the reset function for navigation
    const { registerMultiReferenceReset } = useFormReset();

    // Register this component's reset function
    useEffect(() => {
        const unregister = registerMultiReferenceReset(resetInitialLoad);
        return unregister;
    }, [registerMultiReferenceReset, resetInitialLoad]);

    // Update the temporary loading state when we get the real one
    useEffect(() => {
        setTempLoadingState(loadingInitialItems);
    }, [loadingInitialItems]);

    const { getValues } = useFormContext();
    const columns = useMultiReferenceColumns(
        attribute,
        memberAttributes,
        fieldPath,
        fields,
        organization,
        remove,
        user,
        readonly || false,
        getValues
    );

    const requiredAttributes = memberAttributes.filter(attr => (attr.params?.isMandatory === true && !attr.additionalParams?.readonly));
    const multiRef = useFocusOnAttribute<HTMLDivElement>(attribute);

    // Focus on the first column of newly added row
    useFocusOnNewRow({
        fields,
        fieldPath,
        memberAttributes,
        readonly,
    });

    useEffect(() => {
        // Extend the referenced items so we can show all the attribute values of those items
        extendReferencedItems(currentValue);

        // For reverse hierarchy attributes, we need to re-run when the current item changes or when hierarchy data changes
        //eslint-disable-next-line
    }, isReverseMultiReferenceAttribute ? [currentItemPrimaryKey, hierarchyData, hierarchyLoading, hierarchyError] : []);

    // Enhanced effect to handle data restoration and preservation of newly added items
    useEffect(() => {
        handleDataRestoration(currentValue);
    }, [isReverseMultiReferenceAttribute, currentValue, fieldPath, setValue, handleDataRestoration]);

    // Update referenced item IDs for real-time validation updates
    useEffect(() => {
        updateReferencedItemIds(currentValue);
    }, [currentValue, updateReferencedItemIds]);

    const handleItemAdded = (item: ReferenceItemOption) => {
        appendItem(item);
        // Cache the newly added item for reverse hierarchy attributes
        cacheNewlyAddedItem(item);
        setEnableSorting(false);
        setNewlyCreatedItem(null);
        // Navigate to last page if pagination is enabled
        // Use setTimeout to ensure the Grid component has updated with the new data
        setTimeout(() => {
            const paginationThreshold = 10;
            const willShowPagination = (fields.length + 1) > paginationThreshold;
            if (willShowPagination) {
                gridRef.current?.goToLastPage();
            }
        }, 0);
    };

    const handleItemCreated = (item: ReferenceItemOption) => {
        setNewlyCreatedItem(item);
        setIsCreateNewItemDialogOpen(false);
        setIsDialogOpen(true);
    };

    const handleAddItemDialogClose = (open: boolean) => {
        setIsDialogOpen(open);
        if (!open) {
            setNewlyCreatedItem(null);
        }
    };

    if (hidden) {
        return null;
    }

    if (isEmpty(referenceItemCategories)) {
        logger.error(`MultiReference: Attribute '${attribute.name}' has no referenced item categories`);
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={false}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={false}
            labelClassName={labelClassName}
            actionComponent={!readonly ? <MultiReferenceToolbar
                onOpenSelectDialog={() => setIsDialogOpen(true)}
                onOpenCreateDialog={() => setIsCreateNewItemDialogOpen(true)}
                fieldPath={fieldPath}
                hierarchyAttributeName = {attribute.name}
            /> : null}
        >
            {
                (loadingInitialItems || (isReverseMultiReferenceAttribute && hierarchyLoading)) ?
                    <div className="tw-flex tw-items-center tw-justify-center">
                        <Loader />
                    </div>
                    :
                    <div ref={multiRef} tabIndex={-1}>
                        <MultiReferenceGrid
                            ref={gridRef}
                            columns={columns}
                            data={fields || []}
                            fieldPath={fieldPath}
                            memberAttributes={memberAttributes}
                            attribute={attribute}
                            enableSorting={enableSorting}
                        />

                        <MultiReferenceDialogs
                            isDialogOpen={isDialogOpen}
                            isCreateNewItemDialogOpen={isCreateNewItemDialogOpen}
                            onDialogOpenChange={handleAddItemDialogClose}
                            onCreateNewItemDialogOpenChange={setIsCreateNewItemDialogOpen}
                            attribute={attribute}
                            readonly={readonly}
                            placeholder={placeholder}
                            requiredAttributes={requiredAttributes}
                            onItemAdded={handleItemAdded}
                            onItemCreated={handleItemCreated}
                            newlyCreatedItem={newlyCreatedItem}
                        />
                    </div>
            }
        </Layout>
    );
};

export { MultiReferenceComponent };
